// src/pages/CustomerView.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Row, Col, Card, Tag, Button, Typography, Space, Skeleton, Segmented, message,
  Avatar, Divider, Tooltip, Badge,
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReloadOutlined, CalendarOutlined, PlusOutlined,
  UserOutlined, PhoneOutlined, MailOutlined, EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import StandardPage from '../components/Layout/StandardPage';
import CreateActivityDrawer from '../components/activities/CreateActivityDrawer';
import EditCustomerDrawer from '../components/customers/EditCustomerDrawer';
import FileManager from '../components/files/FileManager';
import ActivitiesList from '../components/activities/ActivitiesList';
import ActivitiesOverview from '../components/activities/ActivitiesOverview';

import { getCustomer, type CustomerDTO } from '../services/customers';
import { listActivitiesByCustomer, type ActivityDTO, type Page } from '../services/activities';

// NEW: Drawer per gestire le Views
import CustomerViewsDrawer from '../components/views/CustomerViewsDrawer';

// NEW: Drawer Gestione Accessi
import ManageCustomerAccessDrawer from '../components/users/ManageCustomerAccessDrawer';

const { Text } = Typography;

const CustomerView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const customerId = Number(id);

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState<CustomerDTO | null>(null);

  // activities
  const [actPage, setActPage] = useState(1);
  const [actSize, setActSize] = useState(10);
  const [acts, setActs] = useState<Page<ActivityDTO> | null>(null);
  const [loadingActs, setLoadingActs] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('table');

  // refresh token per aggiornare anche ActivitiesOverview
  const [reloadToken, setReloadToken] = useState(0);

  // drawers
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  // NEW: stato per il drawer delle Views
  const [viewsDrawerOpen, setViewsDrawerOpen] = useState(false);
  // NEW: stato per il drawer Gestione Accessi
  const [accessDrawerOpen, setAccessDrawerOpen] = useState(false);

  const fetchCustomer = useCallback(async () => {
    try {
      setLoading(true);
      const c = await getCustomer(customerId);
      setCustomer(c);
    } catch (e: any) {
      message.error(e?.message || 'Errore caricamento cliente');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  const fetchActivities = useCallback(
    async (page = actPage, size = actSize) => {
      try {
        setLoadingActs(true);
        const data = await listActivitiesByCustomer(customerId, page - 1, size);
        setActs(data);
      } catch (e: any) {
        message.error(e?.message || 'Errore caricamento attività');
      } finally {
        setLoadingActs(false);
      }
    },
    [customerId, actPage, actSize]
  );

  useEffect(() => {
    if (!customerId || Number.isNaN(customerId)) {
      message.error('ID cliente non valido');
      navigate('/customers');
      return;
    }
    fetchCustomer();
    fetchActivities(1, actSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const breadcrumbs = [
    { title: 'Clienti', href: '/customers' },
    { title: customer?.businessName || `Cliente #${customerId}` },
  ];

  const refreshAll = useCallback(() => {
    fetchCustomer();
    fetchActivities(1, actSize);
    setReloadToken(t => t + 1); // aggiorna anche le statistiche
  }, [fetchCustomer, fetchActivities, actSize]);

  const actions = [
    {
      label: 'Nuova Attività',
      type: 'primary' as const,
      icon: <PlusOutlined />,
      onClick: () => setDrawerOpen(true),
    },
    {
      label: 'Ricarica',
      type: 'default' as const,
      icon: <ReloadOutlined />,
      onClick: () => refreshAll(),
    }
  ];

  const getCustomerInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  return (
    <>
      <StandardPage
        title=""
        showIndietro="Indietro"
        breadcrumbs={breadcrumbs}
        actions={actions}
        showBackButton
      >
        {/* Header cliente */}
        <Card
          className="customer-header-card"
          style={{
            borderRadius: 16,
            marginBottom: 24,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            color: 'white'
          }}
          bodyStyle={{ padding: '32px' }}
        >
          {loading ? (
            <Skeleton avatar active paragraph={{ rows: 2 }} />
          ) : (
            <Row align="middle" gutter={[24, 16]}>
              <Col>
                <Badge
                  dot
                  color={customer?.isActive ? '#52c41a' : '#ff4d4f'}
                  offset={[-8, 8]}
                >
                  <Avatar
                    size={80}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      fontSize: '28px',
                      fontWeight: 'bold'
                    }}
                  >
                    {customer?.businessName ? getCustomerInitials(customer.businessName) : <UserOutlined />}
                  </Avatar>
                </Badge>
              </Col>
              <Col flex={1}>
                <Typography.Title level={2} style={{ color: 'white', margin: 0, marginBottom: 8 }}>
                  {customer?.businessName || 'Cliente'}
                </Typography.Title>
                <Space direction="vertical" size={4}>
                  <Space>
                    <MailOutlined style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
                    <Text style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      {customer?.email || 'Email non disponibile'}
                    </Text>
                  </Space>
                  {customer?.phone && (
                    <Space>
                      <PhoneOutlined style={{ color: 'rgba(255, 255, 255, 0.8)' }} />
                      <Text style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {customer.phone}
                      </Text>
                    </Space>
                  )}
                </Space>
              </Col>
              <Col>
                <Space direction="vertical" align="end">
                  {/* APRE IL NUOVO DRAWER VIEWS */}
                  <Button onClick={() => setViewsDrawerOpen(true)}>Inserisci views</Button>
                  <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '12px' }}>
                    Cliente dal {customer?.createdAt ? dayjs(customer.createdAt).format('DD/MM/YYYY') : '—'}
                  </Text>
                </Space>
              </Col>
            </Row>
          )}
        </Card>

        {/* Panoramica Attività */}
        <div style={{ marginBottom: 24 }}>
          <ActivitiesOverview customerId={customerId} refreshKey={reloadToken} />
        </div>

        <Row gutter={[24, 24]}>
          <Col xs={24} xl={8}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
              {/* Dettagli Cliente */}
              <Card
                title={<Space><UserOutlined /><span>Dettagli Cliente</span></Space>}
                extra={
                  
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      size="small"
                      onClick={() => setEditDrawerOpen(true)}
                    >Modifica </Button>
             
                }
                style={{ borderRadius: 12 }}
                headStyle={{ borderBottom: '1px solid #f0f0f0' }}
              >
                {loading ? (
                  <Skeleton active />
                ) : (
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ color: '#1890ff', marginBottom: 8, display: 'block' }}>
                        Note cliente
                      </Text>
                      <Space direction="vertical" size={4} style={{ marginLeft: 20 }}>
                        <Text >
                          {customer?.notes ? customer.notes : <Text type="secondary">Nessuna nota disponibile</Text>}
                        </Text>
                        {customer?.phone && (
                          <Text copyable={{ text: customer.phone }}>
                            Tel: {customer.phone}
                          </Text>
                        )}
                      </Space>
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    <div>
                      <Text strong style={{ color: '#1890ff', marginBottom: 8, display: 'block' }}>
                        Credenziali
                      </Text>
                      <div style={{ marginLeft: 20 }}>
                        {customer?.credentials ? (
                          <Text style={{ whiteSpace: 'pre-wrap' }}>
                            {customer.credentials}
                          </Text>
                        ) : (
                          <Text type="secondary">Credenziali non disponibili</Text>
                        )}
                      </div>
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    {/* === Accessi Cliente + Bottone Gestisci Accessi === */}
                    <div>
                      <Text strong style={{ color: '#1890ff', marginBottom: 8, display: 'block' }}>
                        Accessi cliente
                      </Text>
                      <div style={{ marginLeft: 20 }}>
                        {customer?.hasUserAccess ? (
                          <Space direction="vertical" size={6}>
                            <Text>Email: {customer.userEmail}</Text>
                            <Button size="middle" onClick={() => setAccessDrawerOpen(true)}>
                              Gestisci accessi
                            </Button>
                          </Space>
                        ) : (
                          <Space direction="vertical" size={6}>
                            <Text type="secondary">Accessi non creati</Text>
                            <Button type="primary" size="middle" onClick={() => setAccessDrawerOpen(true)}>
                              Gestisci accessi
                            </Button>
                          </Space>
                        )}
                      </div>
                    </div>
                  </Space>
                )}
              </Card>

              {/* File manager */}
              <FileManager
                entity="customer"
                entityId={customerId}
                title="Consulenze"
                pageSize={5}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
              />
            </Space>
          </Col>

          <Col xs={24} xl={16}>
            {/* Header semplice (no Card) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Space>
                <CalendarOutlined />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Attività</span>
              </Space>
              <Segmented
                options={[{ label: 'Tabella', value: 'table' }, { label: 'Timeline', value: 'timeline' }]}
                value={viewMode}
                onChange={(v) => setViewMode(v as any)}
              />
            </div>

            {/* Lista attività (no Card) */}
            <ActivitiesList
              data={acts}
              loading={loadingActs}
              viewMode={viewMode}
              showHeader={false}
              onPageChange={(p, s) => {
                setActPage(p);
                setActSize(s);
                fetchActivities(p, s);
              }}
              onRefresh={() => {
                fetchActivities(actPage, actSize);
                setReloadToken(t => t + 1); // aggiorna anche le statistiche
              }}
            />
          </Col>
        </Row>
      </StandardPage>

      {/* Drawer: crea attività */}
      <CreateActivityDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={() => {
          fetchActivities(1, actSize);
          setReloadToken(t => t + 1);
        }}
        defaultCustomerId={customerId}
        defaultCustomerLabel={customer?.businessName}
      />

      {/* Drawer: modifica cliente */}
      <EditCustomerDrawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        customer={customer}
        onUpdated={(u) => setCustomer(u)}
      />

      {/* Drawer VIEWS */}
      <CustomerViewsDrawer
        open={viewsDrawerOpen}
        onClose={() => setViewsDrawerOpen(false)}
        customerId={customerId}
      />

      {/* NEW: Drawer Gestisci Accessi */}
      <ManageCustomerAccessDrawer
        open={accessDrawerOpen}
        onClose={() => setAccessDrawerOpen(false)}
        customer={customer}
        onSaved={() => {
          setAccessDrawerOpen(false);
          fetchCustomer(); // ricarica per riflettere hasUserAccess / userEmail aggiornati
        }}
      />
    </>
  );
};

export default CustomerView;
